import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as adminApi from '../../api/admin'
import Spinner from '../../components/common/Spinner'
import {
  Avatar,
  Badge,
  EmptyState,
  PillGroup,
  SearchInput,
  fmtRelative,
  styles,
  tokens,
} from './_ui'

type HasMerchant = '' | 'yes' | 'no'

export default function AdminUsersPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<adminApi.AdminUserListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [searchDebounce, setSearchDebounce] = useState('')
  const [hasMerchant, setHasMerchant] = useState<HasMerchant>('')
  const isFirstLoad = useRef(true)

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const load = async (append = false) => {
    setLoading(true)
    setError(null)
    try {
      const params: adminApi.AdminUsersParams = {
        skip: append ? items.length : 0,
        search: searchDebounce || undefined,
      }
      if (hasMerchant === 'yes') params.has_merchant = true
      else if (hasMerchant === 'no') params.has_merchant = false
      const data = await adminApi.listAdminUsers(params)
      setTotal(data.total)
      setItems(append ? [...items, ...data.items] : data.items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users')
    } finally {
      setLoading(false)
      isFirstLoad.current = false
    }
  }

  useEffect(() => {
    load(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounce, hasMerchant])

  const hasMore = items.length < total
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasMore || loading) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) load(true)
      },
      { rootMargin: '300px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, items.length, searchDebounce, hasMerchant])

  if (loading && isFirstLoad.current) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div style={{ padding: '8px 16px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          background: 'linear-gradient(135deg, #14b8a620, #3b82f620)',
          borderRadius: 18,
          padding: 18,
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 32, fontWeight: 800, color: tokens.text, letterSpacing: '-0.02em' }}>
          {total}
        </span>
        <span style={{ ...styles.subtle, fontSize: 13 }}>platform accounts</span>
      </div>

      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Name, @username or telegram id…"
      />

      <PillGroup<HasMerchant>
        value={hasMerchant}
        onChange={setHasMerchant}
        options={[
          { value: '', label: 'All' },
          { value: 'yes', label: '🏪 Merchants' },
          { value: 'no', label: '👀 Public' },
        ]}
      />

      {error && (
        <p style={{ fontSize: 13, textAlign: 'center', color: tokens.red, padding: '8px 0' }}>
          {error}
        </p>
      )}

      {!loading && items.length === 0 && !error && (
        <EmptyState emoji="🔎" title="No users found" subtitle="Try a different search" />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((u) => {
          const fullName =
            [u.first_name, u.last_name].filter(Boolean).join(' ') ||
            u.telegram_username ||
            `#${u.telegram_id}`
          return (
            <div
              key={u.id}
              onClick={() => navigate(`/admin/users/${u.id}`)}
              style={{
                background: tokens.cardBg,
                borderRadius: 14,
                padding: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Avatar src={u.photo_url} name={fullName} size={44} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <p
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: tokens.text,
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {fullName}
                  </p>
                  {u.source === 'public' && <Badge>👀 public</Badge>}
                  {u.is_premium && <Badge tone="amber">⭐ premium</Badge>}
                  {!u.is_active && <Badge tone="red">inactive</Badge>}
                  {u.is_mock && <Badge tone="purple">mock</Badge>}
                </div>
                <p
                  style={{
                    fontSize: 12,
                    opacity: 0.5,
                    color: tokens.text,
                    margin: '2px 0 0',
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {u.telegram_username ? `@${u.telegram_username} · ` : ''}
                  {u.telegram_id}
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  {u.owns_shops_count > 0 && <Badge tone="blue">🏪 {u.owns_shops_count}</Badge>}
                  {u.member_of_count > 0 && (
                    <Badge tone="purple">👥 {u.member_of_count}</Badge>
                  )}
                  {u.last_active_at && (
                    <span style={{ fontSize: 11, opacity: 0.5, color: tokens.text }}>
                      seen {fmtRelative(u.last_active_at)}
                    </span>
                  )}
                  {!u.last_active_at && u.created_at && (
                    <span style={{ fontSize: 11, opacity: 0.4, color: tokens.text }}>
                      joined {fmtRelative(u.created_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div ref={sentinelRef} style={{ height: 1 }} />
      {hasMore && loading && (
        <div style={{ ...styles.subtle, fontSize: 12, textAlign: 'center', padding: '8px 0' }}>
          Loading more…
        </div>
      )}
      {!hasMore && items.length > 0 && (
        <div style={{ ...styles.faint, fontSize: 11, textAlign: 'center', padding: '8px 0' }}>
          End of list · {total} users
        </div>
      )}
    </div>
  )
}
